import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import {
  Leaf, Truck, Clock, ShoppingBag, MapPin, Shield,
  CheckCircle, ArrowRight, Zap, CalendarDays,
} from 'lucide-react';
import FlashDealsSection from '../components/common/FlashDealsSection';
import ContactSection from '../components/home/ContactSection';

/* ─── Data ──────────────────────────────────────────────────────────── */

const FEATURES = [
  {
    icon: Leaf,
    title: 'Farm Fresh',
    desc: 'Sourced directly from farms and trusted suppliers for maximum freshness.',
  },
  {
    icon: Shield,
    title: 'Quality Assured',
    desc: 'Every product is hand-picked and checked for quality before delivery.',
  },
  {
    icon: Zap,
    title: 'Instant Delivery',
    desc: 'Need it now? On-demand orders are dispatched immediately.',
  },
  {
    icon: CalendarDays,
    title: 'Scheduled Delivery',
    desc: 'Plan ahead — choose a date and time slot that fits your routine.',
  },
];

const STEPS = [
  {
    number: '01',
    title: 'Browse Products',
    desc: 'Explore a wide range of fresh seasonal fruits and vegetables on the app.',
    icon: ShoppingBag,
  },
  {
    number: '02',
    title: 'Place Your Order',
    desc: 'Choose instant or scheduled delivery, confirm your address, and pay securely.',
    icon: CheckCircle,
  },
  {
    number: '03',
    title: 'Get It Delivered',
    desc: 'Fresh produce arrives at your doorstep — quality guaranteed.',
    icon: Truck,
  },
];

/* ─── Sub-components ─────────────────────────────────────────────────── */

const FeatureCard = ({ icon: Icon, title, desc }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300 group">
    <div className="w-12 h-12 rounded-xl bg-[#f2f8eb] flex items-center justify-center mb-4 group-hover:bg-[#548B2E] transition-colors duration-300">
      <Icon className="w-6 h-6 text-[#548B2E] group-hover:text-white transition-colors duration-300" />
    </div>
    <h3 className="font-semibold text-gray-800 mb-2">{title}</h3>
    <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
  </div>
);

FeatureCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  desc: PropTypes.string.isRequired,
};

const StepCard = ({ number, title, desc, icon: Icon, isLast }) => (
  <div className="flex gap-5">
    <div className="flex flex-col items-center">
      <div className="w-12 h-12 rounded-full bg-[#548B2E] flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-white" />
      </div>
      {!isLast && <div className="w-px flex-1 bg-[#d4e9bb] mt-3" />}
    </div>
    <div className="pb-10">
      <span className="text-xs font-bold text-[#548B2E] tracking-widest uppercase">{number}</span>
      <h3 className="text-lg font-semibold text-gray-800 mt-0.5 mb-1">{title}</h3>
      <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
    </div>
  </div>
);

StepCard.propTypes = {
  number: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  desc: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  isLast: PropTypes.bool.isRequired,
};

/* ─── Page ───────────────────────────────────────────────────────────── */

const Home = () => {
  const aboutRef = useRef(null);
  const { hash } = useLocation();

  const scrollToAbout = () => {
    aboutRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (hash === '#about') {
      setTimeout(() => aboutRef.current?.scrollIntoView({ behavior: 'smooth' }), 150);
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className="bg-white">

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1542838132-92c53300491e?w=1800&q=80"
          alt="Fresh vegetables"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />

        <div className="relative z-10 container mx-auto px-6 lg:px-12 pt-20">
          <div className="max-w-2xl">
            <span className="inline-block text-[#FF9800] text-sm font-semibold tracking-widest uppercase mb-4">
              Jaipur's Fresh Produce App
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Farm Fresh Produce,{' '}
              <span className="text-[#8BC34A]">Delivered</span>{' '}
              to Your Door
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed mb-8 max-w-xl">
              Browse and order fresh fruits and vegetables directly from farms
              and trusted suppliers — delivered instantly or on your schedule.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={scrollToAbout}
                className="inline-flex items-center gap-2 border-2 border-white/70 hover:border-white text-white font-semibold px-7 py-3.5 rounded-full transition-colors duration-200"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
          <div className="w-px h-8 bg-white/40" />
          <div className="w-1.5 h-1.5 rounded-full bg-white/40" />
        </div>
      </section>

      {/* ── FEATURES BAR ─────────────────────────────────────────────── */}
      <section className="py-16 bg-[#f8fdf4]">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f) => (
              <FeatureCard key={f.title} {...f} />
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT BOOKMYFRESH ─────────────────────────────────────────── */}
      <section ref={aboutRef} id="about" className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-14 items-center">

            {/* Text */}
            <div>
              <span className="text-[#548B2E] text-sm font-semibold tracking-widest uppercase">
                About BookMyFresh
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mt-3 mb-6 leading-snug">
                Freshness & Convenience, <br className="hidden lg:block" />Together
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  BookMyFresh is a Jaipur-based application dedicated to delivering
                  fresh fruits and vegetables directly from farms and trusted suppliers
                  to your doorstep. With a focus on quality, convenience, and freshness,
                  users can easily browse and order a wide range of seasonal produce
                  through the app.
                </p>
                <p>
                  Whether you want to plan your groceries in advance or need fresh
                  produce instantly, BookMyFresh offers both scheduled delivery and
                  instant delivery services to suit your daily needs. Orders can be
                  placed anytime for quick and hassle-free delivery, ensuring customers
                  enjoy fresh, healthy, and high-quality products without the stress of
                  traditional grocery shopping.
                </p>
                <p className="font-medium text-gray-700">
                  BookMyFresh brings convenience, freshness, and reliability together
                  to make everyday grocery shopping simple and efficient.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                {[
                  { icon: MapPin, text: 'Jaipur-based' },
                  { icon: Leaf, text: 'Farm Sourced' },
                  { icon: Clock, text: 'Same-day Delivery' },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-2 text-sm text-[#548B2E] font-medium">
                    <Icon className="w-4 h-4" />
                    {text}
                  </div>
                ))}
              </div>
            </div>

            {/* Image grid */}
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1610832958506-aa56368176cf?w=600&q=80"
                alt="Fresh vegetables"
                className="rounded-2xl object-cover w-full h-52 shadow-md"
              />
              <img
                src="https://images.unsplash.com/photo-1518977676601-b53f82aba655?w=600&q=80"
                alt="Fresh potatoes"
                className="rounded-2xl object-cover w-full h-52 shadow-md mt-8"
              />
              <img
                src="https://images.unsplash.com/photo-1464965911861-746a04b4bca6?w=600&q=80"
                alt="Fruits and vegetables"
                className="rounded-2xl object-cover w-full h-52 shadow-md"
              />
              <img
                src="https://images.unsplash.com/photo-1488459716781-31db52582fe9?w=600&q=80"
                alt="Fresh herbs"
                className="rounded-2xl object-cover w-full h-52 shadow-md mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────── */}
      <section className="py-20 bg-[#f8fdf4]">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-14 items-start">

            <div>
              <span className="text-[#548B2E] text-sm font-semibold tracking-widest uppercase">
                Simple Process
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mt-3 mb-4 leading-snug">
                How BookMyFresh Works
              </h2>
              <p className="text-gray-500 mb-10 leading-relaxed">
                Getting farm-fresh produce delivered has never been easier.
                Three simple steps stand between you and the freshest groceries.
              </p>
              <div>
                {STEPS.map((step, i) => (
                  <StepCard key={step.number} {...step} isLast={i === STEPS.length - 1} />
                ))}
              </div>
            </div>

            {/* Delivery options */}
            <div className="space-y-5 lg:pt-12">
              {/* On-Demand card */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-1">On-Demand Delivery</h3>
                    <p className="text-sm text-gray-500 mb-3">
                      Immediate dispatch — your fresh produce arrives at your door as quickly as possible.
                    </p>
                    <ul className="space-y-1">
                      {['Order anytime', 'Immediate dispatch', 'Threshold free delivery', 'No minimum order'].map((pt) => (
                        <li key={pt} className="flex items-center gap-2 text-xs text-gray-600">
                          <CheckCircle className="w-3.5 h-3.5 text-[#548B2E] flex-shrink-0" />
                          {pt}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Scheduled card */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-[#548B2E] hover:shadow-md transition-shadow duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#f2f8eb] flex items-center justify-center flex-shrink-0">
                    <CalendarDays className="w-6 h-6 text-[#548B2E]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-800">Scheduled Delivery</h3>
                      <span className="text-[10px] font-bold bg-[#548B2E] text-white px-2 py-0.5 rounded-full">FLEXIBLE</span>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">
                      Plan your groceries ahead — pick a date and time slot that works for you.
                    </p>
                    <ul className="space-y-1">
                      {['Choose your preferred slot', 'Threshold free delivery', 'Dynamic minimum order', '3-day advance booking'].map((pt) => (
                        <li key={pt} className="flex items-center gap-2 text-xs text-gray-600">
                          <CheckCircle className="w-3.5 h-3.5 text-[#548B2E] flex-shrink-0" />
                          {pt}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <Link
                to="/shop"
                className="flex items-center justify-center gap-2 w-full bg-[#548B2E] hover:bg-[#3f6b20] text-white font-semibold py-3.5 rounded-full transition-colors duration-200"
              >
                Start Shopping <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── FLASH DEALS ──────────────────────────────────────────────── */}
      <div id="flash-deals">
        <FlashDealsSection />
      </div>

      {/* ── CONTACT ──────────────────────────────────────────────────── */}
      <ContactSection />

    </div>
  );
};

export default Home;
